import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import useProjectFollowUpTasks from '../../hooks/portfolio/useProjectFollowUpTasks'
import sortOpenProjectTasks from '../../helpers/portfolio/sortOpenProjectTasks'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardEmptyState from './DashboardCardEmptyState'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/deccos-projectafspraken.md

const DATE_OPTIONS = { year: 'numeric', month: 'numeric', day: 'numeric' }

const columns = [
    { key: 'task', label: 'AFSPRAAK' },
    { key: 'project', label: 'OMGEVING' },
    { key: 'date', label: 'DATUM', searchable: false },
]

/**
 * Zonder datum is de afspraak nog niet ingepland — dat is een eigen toestand,
 * geen ontbrekende waarde.
 *
 * @param {{Timestamp?: {toDate?: () => Date}|null}} task
 * @returns {string}
 */
const describeDate = (task) => task?.Timestamp?.toDate
    ? task.Timestamp.toDate().toLocaleDateString('nl-NL', DATE_OPTIONS)
    : 'Nog geen datum'

/**
 * Alle nog openstaande afspraken die deze omgeving bij haar projecten
 * vastlegde, over alle projecten heen. Eerstvolgende bovenaan.
 */
const DECCOS_MANAGEMENT_TENANT = '123423HJKHVKH'

const DashboardOpenProjectTasksCard = () => {
    const { portfolio } = useSettings()

    // Dezelfde poort als de Overzicht-kaart. De zichtbaarheidsregel houdt deze
    // kaart nu bij de beheeromgeving, maar de zoekopdracht moet ook kloppen als
    // die regel ooit ruimer wordt.
    const tasks = useProjectFollowUpTasks(client === DECCOS_MANAGEMENT_TENANT || portfolio === 'block')

    const openTasks = useMemo(() => sortOpenProjectTasks(tasks), [tasks])

    // 'Geen openstaande afspraken' is een bewering; die mag pas als de gegevens
    // er echt zijn. Let op: de Table draagt met `emptyMessage` een TWEEDE lege
    // staat, dus die tak moet er ook achter.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loaded = Array.isArray(tasks)
    const isEmpty = loaded && openTasks.length === 0

    return (
        <div>
            <StaticCardLabel
                title='Openstaande afspraken'
                pill={{ variant: 'portfolio', label: 'Portfolio' }}
            />
            {!loaded ? (
                <p className='score-explain-card__placeholder'>Laden…</p>
            ) : isEmpty ? (
                <DashboardCardEmptyState type='open-project-tasks' />
            ) : (
                <Table
                    columns={columns}
                    items={openTasks}
                    getItemKey={(task) => task.ID}
                    searchAccessor={(task) => `${task?.Title || ''} ${task?.ProjectName || ''}`}
                    searchPlaceholder='Zoeken op afspraak of omgeving'
                    emptyMessage='Geen openstaande afspraken.'
                    renderRow={(task) => (
                        <>
                            <TableCell>{task.Title || 'Naamloze afspraak'}</TableCell>
                            <TableCell>
                                {task.ProjectID ? (
                                    <Link to={`/${client}/portfolioprojectdetails/${task.ProjectID}`}>
                                        {task.ProjectName || '—'}
                                    </Link>
                                ) : (task.ProjectName || '—')}
                            </TableCell>
                            <TableCell>{describeDate(task)}</TableCell>
                        </>
                    )}
                />
            )}
        </div>
    )
}

export default DashboardOpenProjectTasksCard
